import { type CSSProperties, memo, type ReactNode, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { SortDirection } from '@air/api/types';
import { cn } from '@air/utils-tailwind';

import { SortButton } from '~/components/SortButton/SortButton';
import { TableCell } from '~/components/TableView/TableCell';
import { setSortingFieldAction } from '~/store/configViews/actions';
import { sortingConfigViewOptionsSelector } from '~/store/configViews/selectors';
import { getDefaultSortDirection } from '~/store/configViews/utils';

type SortableHeaderProps = {
  title: string;
  sortingType: string;
  className?: string;
  style?: CSSProperties;
  resizeHandle?: ReactNode;
};

export const SortableHeader = memo(({ title, sortingType, className, resizeHandle, ...props }: SortableHeaderProps) => {
  const dispatch = useDispatch();
  const sortingConfigViewOptions = useSelector(sortingConfigViewOptionsSelector);
  const sortingConfigViewOption = sortingConfigViewOptions.find((opt) => opt.fieldName === sortingType);
  const sortingConfigDirection = sortingConfigViewOption?.sortDirection ?? getDefaultSortDirection(sortingType);

  const onSortToggle = useCallback(() => {
    // User clicked on a header that is not currently used for sorting
    // - set the sorting field
    // - set the default sorting direction, if available
    if (sortingConfigViewOption?.isCurrentSortedOption === false) {
      dispatch(setSortingFieldAction({ sortingFieldName: sortingType, direction: sortingConfigDirection }));
    } else {
      // User clicked on a header that is currently used for sorting
      // - toggle the sorting direction
      if (sortingConfigDirection === SortDirection.desc) {
        dispatch(setSortingFieldAction({ sortingFieldName: sortingType, direction: SortDirection.asc }));
      } else {
        dispatch(setSortingFieldAction({ sortingFieldName: sortingType, direction: SortDirection.desc }));
      }
    }
  }, [dispatch, sortingConfigDirection, sortingType, sortingConfigViewOption]);

  if (!sortingConfigViewOption) return null;

  return (
    <TableCell className={cn('p-1.5 px-2', className, !!resizeHandle && 'relative')} {...props}>
      <SortButton
        isActive={sortingConfigViewOption.isCurrentSortedOption}
        direction={sortingConfigDirection}
        onClick={onSortToggle}
        type={title}
      >
        {title}
      </SortButton>
      {resizeHandle}
    </TableCell>
  );
});

SortableHeader.displayName = 'SortableHeader';
